import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import {
  FRACTION_ONE,
  FRACTION_ZERO,
  fractionMax,
} from "@aprao/common/src/math/fraction.js";
import { opposite } from "@aprao/common/src/math/opposite.js";
import { sum } from "@aprao/common/src/math/sum.js";
import { makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";
import { mapForNullable } from "option-t/nullable";

import type {
  LenderFeeKeys,
  LenderFeePaymentSchedule,
} from "../shapes/ShapeLender.js";
import type { AppraisalDocumentFacade } from "./AppraisalDocumentFacade.js";
import { sumProjectionAmountAtPeriodIndex } from "./CashflowFacade.js";
import { WithRelativesSetDelegate } from "./delegates/WithRelativesSetDelegate.js";
import type { AnyLenderFacade } from "./lender/AnyLenderFacade.js";
import { interestLenderPredicate } from "./lender/interestLenderPredicate.js";
import type { LenderFeeFacade } from "./lender/LenderBaseFacade.js";
import { type ResidualFacade } from "./ResidualFacade.js";
import type { TaxFacade } from "./TaxFacade.js";

export type LenderFeeRelativesDelegate =
  WithRelativesSetDelegate<LenderFeeKeys>;

export class FinanceFacade {
  readonly $stores;

  constructor(stores: {
    documentFacade: AppraisalDocumentFacade;
    taxFacade: TaxFacade;
    residualFacade: ResidualFacade;
  }) {
    makeAutoObservable(this);

    this.$stores = stores;
  }

  getAvailableBalanceByLender = (lender: AnyLenderFacade) => {
    const facilitySize =
      lender.facilitySize.relativesSet.getRelative("absolute");

    return interestLenderPredicate(lender) ?
        mapForNullable(
          sum([facilitySize, opposite(lender.interestAllowance)]),
          // Cap so even if the allowance is bigger than the facility, we don't
          // get bellow zero
          (available) => fractionMax(FRACTION_ZERO, available),
        )
      : facilitySize;
  };

  getLenderFeeRelativesDelegateByStore = computedFn(
    (
      fee: LenderFeeFacade,
      lender: AnyLenderFacade,
    ): LenderFeeRelativesDelegate =>
      new WithRelativesSetDelegate(fee.$model, {
        absolute: () => FRACTION_ONE,
        ratioDebtFundedGdv: () =>
          this.$stores.documentFacade.stages.revenue.getGrossDevelopmentValue(
            "debtFunded",
          ),
        ratioFacilitySize: () =>
          lender.facilitySize.relativesSet.getRelative("absolute"),
      }),
  );

  getLenderFeesTotalByLender = (lender: AnyLenderFacade) =>
    sum(
      lender.fees.map((fee) =>
        this.getLenderFeeRelativesDelegateByStore(fee, lender).getRelative(
          "absolute",
        ),
      ),
    );

  getLendersFeesTotalByPaymentSchedule = (
    filterSchedule: LenderFeePaymentSchedule,
  ) =>
    sum(
      this.$stores.residualFacade.lenders.flatMap((lender) =>
        lender.fees
          .filter(({ paymentSchedule }) => paymentSchedule === filterSchedule)
          .map((fee) =>
            this.getLenderFeeRelativesDelegateByStore(fee, lender).getRelative(
              "absolute",
            ),
          ),
      ),
    );

  get facilitySizeTotal() {
    return sum(
      this.$stores.residualFacade.lenders.map(({ facilitySize }) =>
        facilitySize.relativesSet.getRelative("absolute"),
      ),
    );
  }

  get availableBalanceTotal() {
    return sum(
      this.$stores.residualFacade.lenders.map((lender) =>
        this.getAvailableBalanceByLender(lender),
      ),
    );
  }

  /**
   * The developer's equity contribution.
   *
   * This is the worst case approximation of the required equity. To get a more
   * optimised figure, one should use the cash-flow-based method.
   *
   * The idea is that for serviced payout type the interest is paid with equity
   * during the project. On the other hand, retained is paid early and included
   * in the facility, and rolled-up is paid at the end out of the profit. In both
   *  cases, the developer doesn't need to account of it equity wise.
   */
  get developerContribution() {
    return sum([
      this.$stores.residualFacade.getDevelopmentCostsTotal("debtFunded"),
      this.getLendersFeesTotalByPaymentSchedule("commitment"),
      opposite(this.availableBalanceTotal),
    ]);
  }

  getLendersFeesTotalAtPeriodIndex = (periodIndex: number) => {
    const feeTotalTuples = this.$stores.residualFacade.lenders.flatMap(
      (lender) =>
        lender.fees.map((fee): [LenderFeeFacade, BigFraction | null] => [
          fee,
          this.getLenderFeeRelativesDelegateByStore(fee, lender).getRelative(
            "absolute",
          ),
        ]),
    );

    return sumProjectionAmountAtPeriodIndex(feeTotalTuples, periodIndex);
  };
}
